import * as React from 'react';
import { Label } from '@patternfly/react-core';
import type { Payload, Row, Tier } from '../../../../shared/riskregister/types';

export const DueChip: React.FunctionComponent<{ row: Row }> = ({ row }) => {
  if (row.due_status === 'overdue')
    return (
      <Label className="risk-register__chip risk-register__chip--danger" color="red" isCompact>
        Overdue {Math.abs(row.due_days || 0)}d
      </Label>
    );
  if (row.due_status === 'at-risk')
    return (
      <Label className="risk-register__chip risk-register__chip--warning" color="orange" isCompact>
        Due in {row.due_days}d
      </Label>
    );
  if (row.due_status === 'none')
    return (
      <Label className="risk-register__chip risk-register__chip--neutral" color="grey" isCompact>
        No due date
      </Label>
    );
  if (row.due_status === 'closed')
    return (
      <Label className="risk-register__chip risk-register__chip--success" color="green" isCompact>
        Closed
      </Label>
    );
  return (
    <Label className="risk-register__chip risk-register__chip--info" color="blue" isCompact>
      Due in {row.due_days}d
    </Label>
  );
};

export const IdleChip: React.FunctionComponent<{ row: Row; staleDays: number; stalledDays: number }> = ({
  row,
  staleDays,
  stalledDays,
}) => {
  const label = `Idle ${row.idle}d${row.idle_unknown ? '*' : ''}`;
  if (row.idle >= stalledDays)
    return (
      <Label className="risk-register__chip risk-register__chip--danger" color="red" isCompact>
        {label}
      </Label>
    );
  if (row.idle >= staleDays)
    return (
      <Label className="risk-register__chip risk-register__chip--warning" color="orange" isCompact>
        {label}
      </Label>
    );
  return (
    <Label className="risk-register__chip risk-register__chip--success" color="green" isCompact>
      {label}
    </Label>
  );
};

export const CategoryChip: React.FunctionComponent<{
  row: Row;
  payload: Pick<Payload, 'categories' | 'no_cat_label'>;
}> = ({ row, payload }) => (
  <Label
    className={`risk-register__chip ${row.cat ? 'risk-register__chip--category' : 'risk-register__chip--neutral'}`}
    color={row.cat ? 'purple' : 'grey'}
    isCompact
  >
    {row.cat ? payload.categories[row.cat] : payload.no_cat_label}
  </Label>
);

// Keyed off row.tier, which the payload derives with the register's own sharp_score/high_score
// thresholds. Re-deriving the bands from hardcoded 13/10 here made the chip colour disagree with
// the tier labels and the "High score" filter whenever a threshold changed.
const SCORE_CHIP_STYLES: Record<Tier, { className: string; color: 'red' | 'orange' | 'blue' | 'grey' }> = {
  critical: { className: 'risk-register__chip--danger', color: 'red' },
  high: { className: 'risk-register__chip--warning', color: 'orange' },
  moderate: { className: 'risk-register__chip--info', color: 'blue' },
  unscored: { className: 'risk-register__chip--neutral', color: 'grey' },
};

export const ScoreChip: React.FunctionComponent<{ row: Row }> = ({ row }) => {
  const style = SCORE_CHIP_STYLES[row.tier];
  return (
    <Label className={`risk-register__chip ${style.className}`} color={style.color} isCompact>
      {row.score === null ? 'Unscored' : row.score}
    </Label>
  );
};

export const TierChip: React.FunctionComponent<{ row: Row; payload: Pick<Payload, 'tiers'> }> = ({ row, payload }) => (
  <Label
    className={`risk-register__chip ${row.tier === 'critical' ? 'risk-register__chip--danger' : row.tier === 'high' ? 'risk-register__chip--warning' : row.tier === 'moderate' ? 'risk-register__chip--info' : 'risk-register__chip--neutral'}`}
    color={row.tier === 'critical' ? 'red' : row.tier === 'high' ? 'orange' : row.tier === 'moderate' ? 'blue' : 'grey'}
    isCompact
  >
    {payload.tiers[row.tier].short}
  </Label>
);

export const SourceChip: React.FunctionComponent<{ children: React.ReactNode }> = ({ children }) => (
  <Label className="risk-register__chip risk-register__chip--neutral" color="grey" isCompact>
    {children}
  </Label>
);
